import { store } from '@/store'
import { defineStore } from 'pinia'
import type { RouteRecordItem } from '@/router/types'
import { router } from '@/router'
import { notFoundRoutes } from '@/router/routes/base'
import { API_CENTER_MENUS_ALL } from '@/api/center'
import { safeParseJson } from '@bd/vue3-utils'
import { permissionRoutes } from '@/router/routes/permission'

import { ERROR_404_ROUTE_NAME, LAYOUT_COMPONENT } from '@/router/constant'
import { MENU_TYPE } from '@/constant/menu'
import { basicRoutes } from '@/router/routes/basic'
const LAYOUT_COMPONENT_MAP = {
  [LAYOUT_COMPONENT.MAIN]: () => import('@/layout/MainLayout/index.vue'),
  [LAYOUT_COMPONENT.BLANK]: () => import('@/layout/BlankLayout/index.vue'),
  [LAYOUT_COMPONENT.BASE]: () => import('@/layout/BaseLayout/index.vue'),
  [LAYOUT_COMPONENT.JT_PROJ]: () => import('@/layout/BizLayout/JtProj.vue'),
  [LAYOUT_COMPONENT.JT_ZCH]: () => import('@/layout/BizLayout/JtZch.vue'),
  [LAYOUT_COMPONENT.GY_PROJ]: () => import('@/layout/BizLayout/GyProj.vue'),
  [LAYOUT_COMPONENT.GY_ZCH]: () => import('@/layout/BizLayout/GyZch.vue')
}
export type PermissionMenuItem = Omit<ApiMenu.Item, 'children'> & {
  children: PermissionMenuItem[]
  optionObj: Record<string, any>
}

const normalizeMenus = (menus: ApiMenu.Item[] = []): PermissionMenuItem[] =>
  menus.map(item => {
    const optionObj = safeParseJson(item.option) || {}
    return {
      ...item,
      optionObj,
      children: normalizeMenus(item.children || [])
    }
  })

interface TraverseMenuOptions {
  menus: PermissionMenuItem[]
  keys?: string[]
  menusMap?: Record<string, PermissionMenuItem>
  routesNameMap: Record<string, RouteRecordItem>
}
// 遍历menuTree
const traverseMenus = (options: TraverseMenuOptions) => {
  const { menus, keys = [], menusMap = {}, routesNameMap } = options
  menus.forEach(item => {
    menusMap[item.id!] = item
    if (item.menuPath) {
      keys.push(item.menuPath)
      menusMap[item.menuPath] = item
      if (routesNameMap[item.menuPath]) {
        const routeItem = routesNameMap[item.menuPath]
        const { meta = {} } = routeItem

        item.optionObj = {
          ...item.optionObj,
          ...meta
        }

        routeItem.meta = {
          ...meta,
          title: meta.title || item.menuName,
          icon: meta.icon || item.menuIcon
        }
      }
    }
    if (item.children?.length) {
      traverseMenus({ menus: item.children, menusMap, keys, routesNameMap })
    }
  })
  return {
    keys,
    menusMap
  }
}

interface PermissionState {
  menus: PermissionMenuItem[]
  menusMap: Record<string, PermissionMenuItem>
  menusKey: string[]
  inited: boolean
}
// 生成本地路由映射表
export const genRoutesMap = (routes: RouteRecordItem[]) =>
  routes.reduce(
    (map, route) => {
      map[route.name!] = route
      return map
    },
    {} as Record<string, RouteRecordItem>
  )
const permissionRoutesMap = genRoutesMap(permissionRoutes)

// 递归查找第一个有路径的菜单
const findFirstMenuWithPath = (menus: PermissionMenuItem[]): PermissionMenuItem | null => {
  for (const item of menus) {
    if (item.menuType === MENU_TYPE.MENU && item.menuPath) {
      return item
    }
    if (item.children?.length) {
      const found = findFirstMenuWithPath(item.children)
      if (found) {
        return found
      }
    }
  }
  return null
}
export const usePermissionStore = defineStore({
  id: 'permission',
  state: (): PermissionState => ({
    menus: [],
    menusMap: {},
    menusKey: [],
    inited: false
  }),
  actions: {
    resetState() {
      this.menus = []
      this.menusMap = {}
      this.menusKey = []
      this.inited = false
    },
    async fetchMenus() {
      const rawMenus = await API_CENTER_MENUS_ALL()
      const menus = normalizeMenus(rawMenus)
      this.menus = menus
      const { keys, menusMap } = traverseMenus({
        menus,
        routesNameMap: permissionRoutesMap
      })
      this.menusKey = keys
      this.menusMap = menusMap
    },
    initRoutes() {
      for (const item of permissionRoutes) {
        if (this.menusKey.includes(item.name!)) {
          router.addRoute({
            path: item.path,
            name: item.name + 'Index',
            component: LAYOUT_COMPONENT_MAP[item.meta?.layout || LAYOUT_COMPONENT.MAIN],
            children: [
              {
                ...item,
                path: ''
              }
            ]
          })
        }
      }
      const firstSysMenu = findFirstMenuWithPath(this.menus)
      const redirect = router.resolve({ name: firstSysMenu ? firstSysMenu.menuPath : ERROR_404_ROUTE_NAME }).href
      router.addRoute({
        ...(basicRoutes as any),
        redirect
      })
      notFoundRoutes.forEach(router.addRoute)
    },
    async initPermission() {
      await this.fetchMenus()
      this.initRoutes()
      this.inited = true
    }
  }
})
// Need to be used outside the setup
export function usePermissionStoreOutside() {
  return usePermissionStore(store)
}
